import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Button, DatePicker, Modal, Segmented, Tooltip } from 'antd';
import { ArrowLeftOutlined, DownloadOutlined, LockOutlined } from '@ant-design/icons';
import moment from 'moment';
import { Box } from 'atoms';
import StatusChip from 'componentsv2/common/StatusChip';
import { useWarehousePermissions } from 'hooks';
import { numberWithCommas } from 'utils/NumberCommaSeprator';
import {
	exportWarehouseReportV2,
	getWarehouseProjectV2,
	setWarehouseFreezeV2,
} from 'store/warehousingSliceV2';
import {
	DAILY_TAB,
	FIXED_TAB,
	FREEZE_LOCK_COST,
	FREEZE_LOCK_REVENUE,
	MODEL_COST_PLUS,
	MODEL_LABELS,
	MONTHLY_TAB,
	WAREHOUSING_ROUTE,
} from './warehousing.constants';
import DailyTab from './components/DailyTab';
import MonthlyTab from './components/MonthlyTab';
import FixedTab from './components/FixedTab';
import {
	BackButton,
	DetailHeader,
	ErrorState,
	HeaderActions,
	HeaderLeft,
	HeaderStat,
	HeaderStats,
	PageContainer,
	PageSubtitle,
	PageTitle,
	TabButton,
	TabSwitch,
	TitleRow,
} from './components/warehousingStyles';

const DATE_FORMAT = 'YYYY-MM-DD';

const TABS = [
	{ key: DAILY_TAB, label: 'Daily' },
	{ key: MONTHLY_TAB, label: 'Monthly' },
	{ key: FIXED_TAB, label: 'Fixed' },
];
const VALID_TABS = TABS.map((t) => t.key);

const money = (value) => `₹ ${numberWithCommas(Math.round(Number(value) || 0))}`;

/**
 * Header figures for the project.
 *
 * A project with no entries yet returns nulls rather than numbers, and a row of dashes
 * reads as "broken" on a site that was just created — every missing figure is shown as a
 * real zero, because the project genuinely has zero of it.
 *
 * Cost Plus: `costPlusView` ('company' | 'customer') selects which of
 * `figures.cost_plus_views` backs the header — see that field's docstring comment in
 * warehouse_dashboard_service.py. Its `revenue`/`cost` are the base Cost Plus walk only,
 * so additional charges (Cross Dock) and net ad-hoc adjustments — the same absolute
 * amount in either view, per the PRD — are layered on here, and margin is recomputed
 * from the resulting revenue so the header's own numbers stay internally consistent
 * (revenue = cost + margin).
 */
const toHeaderStats = (figures, costPlusView) => {
	const views = figures?.cost_plus_views;
	if (views) {
		const view = views[costPlusView] || views.company;
		const extras =
			(Number(figures?.mtd_additional_charges) || 0) +
			(Number(figures?.net_revenue_adjustment) || 0);
		const revenue = Number(view.revenue) + extras;
		const costInclMonthly = Number(view.cost);
		const marginAbs = revenue - costInclMonthly;
		return {
			revenue,
			costInclMonthly,
			marginAbs,
			marginPct: revenue ? (marginAbs / revenue) * 100 : 0,
		};
	}
	return {
		revenue: Number(figures?.revenue) || 0,
		costInclMonthly: Number(figures?.cost_incl_monthly) || 0,
		marginAbs: Number(figures?.margin_abs) || 0,
		marginPct: Number(figures?.margin_pct) || 0,
	};
};

const COST_PLUS_VIEW_OPTIONS = [
	{ label: 'Company', value: 'company' },
	{ label: 'Customer', value: 'customer' },
];

export default function WarehouseProjectDetailPageV2() {
	const { projectId } = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [searchParams, setSearchParams] = useSearchParams();
	const { canFreezeWarehouse } = useWarehousePermissions();
	const [freezingLock, setFreezingLock] = useState(null);

	// Tab lives in the URL so it survives a refresh and can be linked to
	const tabFromUrl = searchParams.get('tab');
	const activeTab = VALID_TABS.includes(tabFromUrl) ? tabFromUrl : DAILY_TAB;

	const [periodMonth, setPeriodMonth] = useState(() => moment().startOf('month'));
	const [figures, setFigures] = useState(null);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(false);
	// Cost Plus only (PRD §3.5) — which of figures.cost_plus_views backs the header.
	const [costPlusView, setCostPlusView] = useState('company');

	const periodParam = useMemo(() => periodMonth.format(DATE_FORMAT), [periodMonth]);
	const fromDate = useMemo(
		() => periodMonth.clone().startOf('month').format(DATE_FORMAT),
		[periodMonth]
	);
	const toDate = useMemo(
		() => periodMonth.clone().endOf('month').format(DATE_FORMAT),
		[periodMonth]
	);

	const fetchSummary = useCallback(async () => {
		setLoading(true);
		setLoadError(false);
		try {
			const result = await dispatch(
				getWarehouseProjectV2({
					project_id: Number(projectId),
					period_month: periodParam,
				})
			);
			setFigures(result);
		} catch (error) {
			setLoadError(true);
		} finally {
			setLoading(false);
		}
	}, [dispatch, projectId, periodParam]);

	useEffect(() => {
		fetchSummary();
	}, [fetchSummary]);

	const handleTabChange = (key) => {
		searchParams.set('tab', key);
		setSearchParams(searchParams, { replace: true });
	};

	const handleExport = () =>
		dispatch(
			exportWarehouseReportV2({
				project_id: Number(projectId),
				from_date: fromDate,
				to_date: toDate,
			})
		).catch(() => {
			// Errors surface through the API interceptor
		});

	/**
	 * Engage a lock for the month currently selected in the page's own month picker.
	 * There is no counterpart: a freeze is final, so the button is spent once used and
	 * the server refuses an unfreeze outright. Confirmed first, since the action is
	 * irreversible and scoped to one specific month — the confirmation names that month
	 * explicitly so it's never ambiguous which period is about to be locked.
	 */
	const handleFreeze = (lock) => {
		const monthLabel = periodMonth.format('MMMM YYYY');
		const lockLabel = lock === FREEZE_LOCK_REVENUE ? 'Revenue' : 'Cost';

		Modal.confirm({
			title: `Freeze ${lockLabel} for ${monthLabel}?`,
			icon: <LockOutlined />,
			content: (
				<>
					This freezes <b>{lockLabel}</b> for <b>{monthLabel}</b> only — every other month
					on this project stays fully editable. This cannot be undone once applied.
				</>
			),
			okText: `Freeze ${monthLabel}`,
			okButtonProps: { danger: true },
			cancelText: 'Cancel',
			onOk: async () => {
				setFreezingLock(lock);
				try {
					await dispatch(
						setWarehouseFreezeV2({
							project_id: Number(projectId),
							lock,
							action: 'freeze',
							period_month: periodParam,
						})
					);
					await fetchSummary();
				} catch (error) {
					// Errors surface through the API interceptor
				} finally {
					setFreezingLock(null);
				}
			},
		});
	};

	const modelType = figures?.revenue_model?.model_type;
	const isCostPlus = modelType === MODEL_COST_PLUS;
	const stats = useMemo(() => toHeaderStats(figures, costPlusView), [figures, costPlusView]);

	const renderTab = () => {
		if (activeTab === MONTHLY_TAB) {
			return (
				<MonthlyTab
					projectId={Number(projectId)}
					periodMonth={periodMonth}
					figures={figures}
					loading={loading}
					onDataChanged={fetchSummary}
				/>
			);
		}
		if (activeTab === FIXED_TAB) {
			return (
				<FixedTab
					projectId={Number(projectId)}
					revenueModel={figures?.revenue_model}
					fixedCosts={figures?.fixed_costs}
					dashboard={figures}
					loading={loading}
					onDataChanged={fetchSummary}
				/>
			);
		}
		return (
			<DailyTab
				projectId={Number(projectId)}
				projectName={figures?.project_name}
				periodMonth={periodMonth}
				dashboard={figures}
				onDataChanged={fetchSummary}
			/>
		);
	};

	return (
		<PageContainer>
			<DetailHeader>
				<HeaderLeft>
					<BackButton
						as={ArrowLeftOutlined}
						onClick={() => navigate(WAREHOUSING_ROUTE)}
					/>
					<Box>
						<TitleRow>
							<PageTitle title={figures?.project_name || undefined}>
								{figures?.project_name || 'Warehouse Project'}
							</PageTitle>
							{modelType && (
								<StatusChip
									tone="primary"
									label={MODEL_LABELS[modelType] || modelType}
								/>
							)}
						</TitleRow>
						<PageSubtitle
							title={figures?.client_name || figures?.project_code || undefined}
						>
							{figures?.client_name || figures?.project_code || '—'}
						</PageSubtitle>
					</Box>
				</HeaderLeft>

				<HeaderActions>
					{isCostPlus && figures?.cost_plus_views && (
						<Segmented
							value={costPlusView}
							onChange={setCostPlusView}
							options={COST_PLUS_VIEW_OPTIONS}
						/>
					)}

					<HeaderStats>
						<HeaderStat>
							<div className="stat-label">MTD Revenue</div>
							<div className="stat-value">{money(stats.revenue)}</div>
						</HeaderStat>
						<HeaderStat>
							<div className="stat-label">MTD Cost</div>
							<div className="stat-value">{money(stats.costInclMonthly)}</div>
						</HeaderStat>
						<HeaderStat tone={stats.marginPct < 0 ? 'danger' : 'success'}>
							<div className="stat-label">Margin</div>
							<div className="stat-value">
								{`${money(stats.marginAbs)} · ${stats.marginPct.toFixed(1)}%`}
							</div>
						</HeaderStat>
					</HeaderStats>

					<Tooltip
						title={
							figures?.revenue_frozen
								? `Revenue is frozen for ${periodMonth.format(
										'MMMM YYYY'
								  )} — a freeze cannot be reversed. Other months are unaffected.`
								: (!canFreezeWarehouse &&
										'Only Finance and Admin users can freeze revenue') ||
								  undefined
						}
					>
						<Button
							size="small"
							icon={<LockOutlined />}
							loading={freezingLock === FREEZE_LOCK_REVENUE}
							disabled={Boolean(figures?.revenue_frozen) || !canFreezeWarehouse}
							onClick={() => handleFreeze(FREEZE_LOCK_REVENUE)}
						>
							{figures?.revenue_frozen ? 'Revenue Frozen' : 'Freeze Revenue'}
						</Button>
					</Tooltip>

					<Tooltip
						title={
							figures?.cost_frozen
								? `Cost is frozen for ${periodMonth.format(
										'MMMM YYYY'
								  )} — a freeze cannot be reversed. Other months are unaffected.`
								: (!canFreezeWarehouse &&
										'Only Finance and Admin users can freeze cost') ||
								  undefined
						}
					>
						<Button
							size="small"
							icon={<LockOutlined />}
							loading={freezingLock === FREEZE_LOCK_COST}
							disabled={Boolean(figures?.cost_frozen) || !canFreezeWarehouse}
							onClick={() => handleFreeze(FREEZE_LOCK_COST)}
						>
							{figures?.cost_frozen ? 'Cost Frozen' : 'Freeze Cost'}
						</Button>
					</Tooltip>

					{/* Drives every tab: the figures, the daily range and the dates an
					    entry can be filed against all follow this month. */}
					<DatePicker
						picker="month"
						allowClear={false}
						value={periodMonth}
						format="MMMM YYYY"
						disabledDate={(current) => current && current > moment().endOf('month')}
						onChange={(value) => value && setPeriodMonth(value.startOf('month'))}
					/>

					<Button icon={<DownloadOutlined />} onClick={handleExport}>
						Export
					</Button>
				</HeaderActions>
			</DetailHeader>

			{loadError ? (
				<ErrorState>
					<Box mb="12px">Could not load this project&apos;s figures.</Box>
					<Button onClick={fetchSummary}>Retry</Button>
				</ErrorState>
			) : (
				<>
					<TabSwitch>
						{TABS.map((tab) => (
							<TabButton
								key={tab.key}
								type="button"
								active={activeTab === tab.key}
								onClick={() => handleTabChange(tab.key)}
							>
								{tab.label}
							</TabButton>
						))}
					</TabSwitch>

					{renderTab()}
				</>
			)}
		</PageContainer>
	);
}
